import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageBySlug } from "@/hooks/usePagesDB";
import NotFound from "../NotFound";

export default function ContactPage() {
  const { page } = usePageBySlug("kontakt");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>();

  // Hide page if unpublished
  if (page && !page.isPublished) return <NotFound />;

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) newErrors.firstName = "Vorname ist erforderlich";
    if (!formData.lastName.trim()) newErrors.lastName = "Nachname ist erforderlich";
    if (!formData.email.trim()) {
      newErrors.email = "E-Mail ist erforderlich";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Ungültige E-Mail-Adresse";
    }
    if (!formData.subject) newErrors.subject = "Bitte wählen Sie einen Betreff";
    if (!formData.message.trim()) newErrors.message = "Nachricht ist erforderlich";
    if (formData.message.length > 500) newErrors.message = "Maximal 500 Zeichen";
    if (!formData.consent) newErrors.consent = "Bitte akzeptieren Sie die Datenschutzerklärung";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const form = e.currentTarget;
      const data = new FormData(form);
      const params = new URLSearchParams();
      data.forEach((value, key) => {
        params.append(key, value.toString());
      });

      await fetch(form.action, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setErrors({ form: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-[80px] md:pt-[130px]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Header */}
        <div className="mb-8 md:mb-10">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-hj-muted mb-4 font-body" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-hj-red transition-colors">Startseite</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-hj-dark">{page?.title ?? "Kontakt"}</span>
          </nav>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-hj-dark font-heading">
            {page?.title ?? "Kontakt"}
          </h1>
          {page?.content && (
            <p className="text-base md:text-lg text-hj-gray mt-3 max-w-2xl font-body leading-relaxed">
              {page.content}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 md:p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-emerald-600"></i>
                </div>
                <h2 className="text-xl font-bold text-emerald-800 mb-2 font-heading">Vielen Dank!</h2>
                <p className="text-sm text-emerald-700 font-body">
                  Ihre Nachricht wurde erfolgreich übermittelt. Wir werden uns so schnell wie möglich bei Ihnen melden.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ firstName: "", lastName: "", email: "", subject: "", message: "", consent: false });
                    setErrors(undefined);
                  }}
                  className="mt-4 px-5 py-2.5 text-sm font-medium text-emerald-700 border border-emerald-300 rounded-md hover:bg-emerald-100 transition-colors whitespace-nowrap"
                >
                  Neue Nachricht
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                action="https://readdy.ai/api/form/d7sj36kcmq04e0g4elmg"
                method="POST"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {errors?.form && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600 font-body">
                    {errors.form}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">
                      Vorname <span className="text-hj-red">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleChange("firstName", e.target.value)}
                      className={`w-full px-4 py-3 text-sm border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body ${
                        errors?.firstName ? "border-red-300 bg-red-50" : "border-hj-gray-border"
                      }`}
                      placeholder="Ihr Vorname"
                    />
                    {errors?.firstName && <p className="text-xs text-red-500 mt-1 font-body">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">
                      Nachname <span className="text-hj-red">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleChange("lastName", e.target.value)}
                      className={`w-full px-4 py-3 text-sm border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body ${
                        errors?.lastName ? "border-red-300 bg-red-50" : "border-hj-gray-border"
                      }`}
                      placeholder="Ihr Nachname"
                    />
                    {errors?.lastName && <p className="text-xs text-red-500 mt-1 font-body">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">
                    E-Mail <span className="text-hj-red">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className={`w-full px-4 py-3 text-sm border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body ${
                      errors?.email ? "border-red-300 bg-red-50" : "border-hj-gray-border"
                    }`}
                    placeholder="ihre@email.ch"
                  />
                  {errors?.email && <p className="text-xs text-red-500 mt-1 font-body">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">
                    Betreff <span className="text-hj-red">*</span>
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => handleChange("subject", e.target.value)}
                    className={`w-full px-4 py-3 text-sm border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body bg-white ${
                      errors?.subject ? "border-red-300 bg-red-50" : "border-hj-gray-border"
                    }`}
                  >
                    <option value="">Bitte wählen</option>
                    <option value="allgemein">Allgemeine Anfrage</option>
                    <option value="redaktion">Redaktioneller Hinweis</option>
                    <option value="werbung">Werbung & Kooperation</option>
                    <option value="technisch">Technisches Problem</option>
                    <option value="feedback">Feedback</option>
                  </select>
                  {errors?.subject && <p className="text-xs text-red-500 mt-1 font-body">{errors.subject}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-hj-dark mb-1.5 font-body">
                    Nachricht <span className="text-hj-red">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    rows={6}
                    maxLength={500}
                    className={`w-full px-4 py-3 text-sm border rounded-lg focus:outline-none focus:border-hj-red focus:ring-1 focus:ring-hj-red/20 font-body resize-none ${
                      errors?.message ? "border-red-300 bg-red-50" : "border-hj-gray-border"
                    }`}
                    placeholder="Ihre Nachricht..."
                  />
                  <div className="flex items-center justify-between mt-1">
                    {errors?.message ? (
                      <p className="text-xs text-red-500 font-body">{errors.message}</p>
                    ) : (
                      <span></span>
                    )}
                    <p className="text-xs text-hj-muted font-body">
                      {formData.message.length}/500 Zeichen
                    </p>
                  </div>
                </div>

                {/* Consent checkbox */}
                <div>
                  <label className={`flex items-start gap-3 cursor-pointer ${errors?.consent ? "text-red-500" : ""}`}>
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={(e) => handleChange("consent", e.target.checked)}
                      className="w-5 h-5 mt-0.5 text-hj-red rounded border-hj-gray-border focus:ring-hj-red shrink-0"
                    />
                    <span className="text-sm text-hj-gray font-body leading-relaxed">
                      Ich stimme der Verarbeitung meiner Daten gemäss der{" "}
                      <Link to="/datenschutz" className="text-hj-red hover:underline">Datenschutzerklärung</Link>{" "}
                      zu. <span className="text-hj-red">*</span>
                    </span>
                  </label>
                  {errors?.consent && <p className="text-xs text-red-500 mt-1 font-body ml-8">{errors.consent}</p>}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-3 bg-hj-red text-white text-sm font-semibold rounded-lg hover:bg-hj-red-light transition-colors font-body whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? "Wird gesendet..." : "Nachricht senden"}
                </button>
              </form>
            )}
          </div>

          {/* Sidebar Info */}
          <div className="space-y-5 md:space-y-6">
            <div className="bg-hj-gray-light rounded-xl p-5 md:p-6">
              <h3 className="text-base font-bold text-hj-dark mb-4 font-heading">Kontaktinformationen</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-hj-red/10 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-hj-red"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-hj-dark font-body">E-Mail</p>
                    <a href="mailto:redaktion@helvetik-journal.ch" className="text-sm text-hj-red hover:underline font-body">
                      redaktion@helvetik-journal.ch
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-hj-red/10 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-hj-red"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-hj-dark font-body">Adresse</p>
                    <p className="text-sm text-hj-gray font-body">
                      Helvetik Journal AG<br />
                      Bahnhofstrasse 42<br />
                      8001 Zürich<br />
                      Schweiz
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-hj-red/10 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-hj-red"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-hj-dark font-body">Telefon</p>
                    <p className="text-sm text-hj-gray font-body">+41 44 123 45 67</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-hj-gray-light rounded-xl p-5 md:p-6">
              <h3 className="text-base font-bold text-hj-dark mb-4 font-heading">Redaktionelle Leitung</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-hj-dark font-body">Markus Weber</p>
                  <p className="text-xs text-hj-muted font-body">Chefredakteur</p>
                  <a href="mailto:m.weber@helvetik-journal.ch" className="text-sm text-hj-red hover:underline font-body">
                    m.weber@helvetik-journal.ch
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-hj-dark rounded-xl p-5 md:p-6 text-white">
              <h3 className="text-base font-bold mb-2 font-heading">Werbung & Kooperationen</h3>
              <p className="text-sm text-white/70 mb-3 font-body">
                Interessiert an einer Partnerschaft? Erfahren Sie mehr über unsere Werbemöglichkeiten.
              </p>
              <Link
                to="/werbung"
                className="inline-flex items-center gap-1.5 text-sm text-white hover:text-white/80 transition-colors font-body"
              >
                Media Kit ansehen
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}